<template>
  <b-modal
    modal-class="component-modal-product"
    id="productSelectedItemModal"
    ref="productSelectedItemModal"
    size="xl"
    :title="modalTitle"
    no-close-on-backdrop
    @close="close"
  >
    <b-form id="productSelectedTable" @submit.prevent="saveProductSelected">
      <b-form-group label-cols="3" label="เหตุผล:" label-align="right">
        <b-form-textarea
          rows="2"
          max-rows="7"
          class="required"
          id="reasonStock"
          v-model="reasonStock"
          :class="{
            'is-invalid': $v.reasonStock.$error,
          }"
          maxlength="500"
          placeholder="กรอกรายละเอียด"
        ></b-form-textarea>
        <template v-slot:description> </template>
      </b-form-group>
      <b-table
        id="productSelectedTable"
        ref="ProductSelectedTable"
        hover
        small
        sort-icon-left
        head-variant="light"
        :items="productAdjustmentItems"
        :fields="productSelectedFields"
      >
        <template v-slot:cell(index)="row">
          <div class="d-flex align-items-center justify-content-center">
            {{ row.index + 1 }}.
          </div>
        </template>

        <template v-slot:cell(productNoAndName)="row">
          <div class="multiselect-wrapper">
            <multiselect
              class="col p-0 required"
              v-model="row.item.productNoAndName"
              :options="getProductOptions(row.index)"
              :multiple="false"
              placeholder="เลือกสินค้า"
              @input="onProductNoAndNameChange(row)"
              track-by="id"
              :custom-label="item => item.name || ''"
              selectLabel="เลือกสินค้า"
              selectedLabel="เลือกแล้ว"
              deselectLabel="คลิกเพื่อลบออก"
              openDirection="below"
              :class="{
                'is-invalid':
                  $v.productAdjustmentItems.$each[row.index] &&
                  $v.productAdjustmentItems.$each[row.index].productNoAndName
                    .$error,
              }"
            >
              <span slot="noResult">ไม่พบรายการ แนะนำเปลี่ยนคำค้นหา</span>
            </multiselect>
            <template
              v-if="
                $v.productAdjustmentItems.$each[row.index] &&
                $v.productAdjustmentItems.$each[row.index].productNoAndName
                  .$error
              "
            >
              <div class="invalid-icon-position">
                <i class="icon-invalid-exclamation"></i>
              </div>
            </template>
          </div>
        </template>

        <template v-slot:cell(adjustmentAmount)="row">
          <InputNumberWithButtonField
            :adjustStock="row.item.adjustmentAmount"
            :onChange="value => onChangeAdjustStock(row, value)"
            :disabled="!row.item.productNoAndName"
            :invalid="
              $v.productAdjustmentItems.$each[row.index] &&
              $v.productAdjustmentItems.$each[row.index].adjustmentAmount.$error
            "
          />
        </template>

        <template v-slot:cell(currentStockToAfterAdjustment)="row">
          <PreAndPostStockField
            :currentStock="convertUnitStock(row)"
            :adjustStock="row.item.adjustmentAmount"
            :onChange="value => onChangeAdjustStock(row, value)"
            :disabled="!row.item.productNoAndName"
            :invalid="isCurrentStockToAfterAdjustmentInvalid(row)"
          />
        </template>

        <template v-slot:cell(productUnit)="row">
          <multiselect
            class="col p-0 required multiselect-unit"
            v-model="row.item.productUnit"
            :options="buildUomOptions(row.item.productNoAndName)"
            :multiple="false"
            :disabled="!row.item.productNoAndName"
            @input="onProductUnitChange(row)"
            placeholder="เลือกหน่วยสินค้า"
            track-by="id"
            :custom-label="customLabel"
            selectLabel="เลือกหน่วยสินค้า"
            selectedLabel="เลือกแล้ว"
            :allow-empty="false"
            :clear-on-select="false"
            :close-on-select="true"
            openDirection="below"
            deselectLabel=""
            :class="{
              'is-invalid':
                $v.productAdjustmentItems.$each[row.index] &&
                $v.productAdjustmentItems.$each[row.index].productUnit.$error,
            }"
          >
            <span slot="noResult">ไม่พบรายการ แนะนำเปลี่ยนคำค้นหา</span>
          </multiselect>
        </template>

        <template v-slot:cell(buttons)="row">
          <button
            :disabled="row.index === 0 && productAdjustmentItems.length === 1"
            @click="deleteAdjustmentItem(row.item, row.index)"
            class="delete-btn"
          >
            <i class="fas fa-trash-alt"></i>
          </button>
        </template>

        <template v-slot:bottom-row>
          <td colspan="6" class="bottom-row-cell">
            <b-button
              type="button"
              variant="primary"
              @click="addAdjustmentItem"
              class="bottom-add-row"
            >
              <i class="fas fa-plus"></i> เพิ่มรายการสินค้า
            </b-button>
          </td>
        </template>
      </b-table>
    </b-form>

    <template v-slot:modal-footer>
      <b-button type="submit" form="productSelectedTable" variant="primary">
        ยืนยันสต๊อก
      </b-button>
    </template>
    <Dialog ref="Dialog"></Dialog>
    <Loading v-if="isLoading"></Loading>
  </b-modal>
</template>

<script>
import Dialog from "@/components/modal/Dialog";
import Loading from "@/components/Loading";
import { maxLength, required, requiredIf } from "vuelidate/lib/validators";
import InputNumberWithButtonField from "@/components/table/stock/fields/InputNumberWithButtonField.vue";
import PreAndPostStockField from "@/components/table/stock/fields/PreAndPostStockField.vue";
import BigNumber from "bignumber.js";
import { mapActions } from "vuex";

export default {
  name: "ProductSelectedItem",
  components: {
    Dialog,
    Loading,
    InputNumberWithButtonField,
    PreAndPostStockField,
  },
  data() {
    return {
      isLoading: false,
      modalTitle: "ปรับปรุงสต๊อกจากสินค้าที่เลือก",
      productList: [],
      reasonStock: "",
      selectedProducts: [],
      conversionUnitRate: [],
      productAdjustmentItems: [
        {
          productNoAndName: "",
          adjustmentAmount: 0,
          productUnit: null,
        },
      ],
      productSelectedFields: [
        {
          key: "index",
          label: "",
          class: "text-center",
          thStyle: { width: "5%" },
          tdStyle: { width: "5%" },
        },
        {
          key: "productNoAndName",
          label: "รหัสสินค้า - ชื่อสินค้า",
          thStyle: { width: "35%" },
          tdStyle: { width: "35%" },
        },
        {
          key: "adjustmentAmount",
          label: "จำนวนที่ปรับปรุง",
          thStyle: { width: "15%" },
          tdStyle: { width: "15%" },
        },
        {
          key: "currentStockToAfterAdjustment",
          label: "สต๊อก (ปัจจุบัน→หลังปรับปรุง)",
          thStyle: { width: "20%" },
          tdStyle: { width: "20%" },
        },
        {
          key: "productUnit",
          label: "หน่วยสินค้า",
          thStyle: { width: "20%" },
          tdStyle: { width: "20%" },
        },
        {
          key: "buttons",
          label: "",
          class: "text-center",
          thStyle: { width: "5%" },
          tdStyle: { width: "5%" },
        },
      ],
    };
  },
  validations: {
    reasonStock: {
      required,
      maxLength: maxLength(500),
    },
    productAdjustmentItems: {
      $each: {
        productNoAndName: {
          required(value) {
            return !!(value && value.id);
          },
        },
        adjustmentAmount: {
          required(value, row) {
            if (!row || !row.productNoAndName || !row.productNoAndName.id)
              return true;
            return value !== null && Number(value) !== 0;
          },
        },
        currentStockToAfterAdjustment: {
          required(value, row) {
            if (!row || !row.productNoAndName || !row.productNoAndName.id)
              return true;

            const after = Number(value);
            const current = Number(row.productAvailableStock) || 0;
            return after !== current;
          },
        },
        productUnit: {
          required: requiredIf(function (row) {
            return !!(row && row.productNoAndName && row.productNoAndName.id);
          }),
        },
      },
    },
  },
  computed: {
    unitOptions() {
      return (this.unitList || []).map(unit => ({
        id: unit.unitId,
        name: unit.unitNameTh,
      }));
    },
    productOptions() {
      return this.productList.map(product => ({
        id: product.productId,
        name: `${product.productCode} - ${product.productNameMain}`,
      }));
    },
  },
  methods: {
    ...mapActions({
      fetchProductList: "moduleProductInventory/fetchProductList",
      fetchAllGRWithProductList:
        "moduleProductInventory/fetchAllGRWithProductList",
    }),
    onChangeAdjustStock(row, value) {
      const num = Number(value);

      this.$set(row.item, "adjustmentAmount", num);
      const current = Number(row.item.productAvailableStock) || 0;

      const afterAdjustment = new BigNumber(current).plus(num).toFixed(2);
      this.$set(row.item, "currentStockToAfterAdjustment", afterAdjustment);

      const rowV = this.$v.productAdjustmentItems?.$each?.[row.index];
      if (rowV?.adjustmentAmount?.$reset) rowV.adjustmentAmount.$reset();
      if (rowV?.currentStockToAfterAdjustment?.$reset)
        rowV.currentStockToAfterAdjustment.$reset();
    },
    saveProductSelected() {
      this.$v.$touch();
      const reasonStockInvalid = this.$v.reasonStock.$invalid;
      const productAdjustmentItemsInvalid =
        this.$v.productAdjustmentItems.$invalid;

      if (reasonStockInvalid || productAdjustmentItemsInvalid) {
        return;
      }

      this.isLoading = true;

      const adjustmentProducts = this.productAdjustmentItems.map(item => ({
        productId: item.productNoAndName.id,
        qtyUom: item.adjustmentAmount,
        uomId: item.productUnit.id,
      }));

      const adjustStockData = {
        type: "GENERAL",
        reason: this.reasonStock,
        adjustmentProducts: adjustmentProducts,
      };

      this.$store
        .dispatch("moduleProductInventory/createStockAdjustment", {
          clinicUrl: this.$route.params.clinicUrl,
          branchUrl: this.$route.params.branchUrl,
          adjustStockData: adjustStockData,
        })
        .then(res => {
          if (res.status == 201) {
            this.reloadProductList();
            this.reloadGRList();
            this.close();
            this.$parent.showDialogToast("success", "ปรับปรุงสต๊อกสำเร็จ");
          }
        })
        .catch(err => {
          const errorMessage = err.response?.data?.errorMessage;
          const errorMsg =
            typeof errorMessage === "string"
              ? errorMessage
              : "ปรับปรุงสต๊อกไม่สำเร็จ กรุณาลองใหม่";
          this.$parent.showDialogToast("danger", errorMsg);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    show(data) {
      const { productList, selectedProducts } = data;
      this.productList = productList;
      this.selectedProducts = selectedProducts;
      this.getProductSelectedItems();
      this.$refs.productSelectedItemModal.show();
    },
    close() {
      this.$v.$reset();
      this.setDefaultValue();
      this.$refs.productSelectedItemModal.hide();
    },
    reloadProductList() {
      this.fetchProductList({
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
      });
    },
    reloadGRList() {
      this.fetchAllGRWithProductList({
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
      });
    },
    setDefaultValue() {
      this.reasonStock = "";
      this.productAdjustmentItems = [
        {
          productNoAndName: "",
          adjustmentAmount: 0,
          productUnit: null,
        },
      ];
    },
    customLabel(item) {
      return item?.name || "";
    },
    buildUomOptions(productNoAndName) {
      const uoms = [];

      if (!productNoAndName) {
        return this.unitOptions;
      }

      const product = this.productList.find(
        p => p.productId === productNoAndName.id
      );

      if (!product) return uoms;

      if (product.productUnit) {
        uoms.push({
          id: product.productUnit.id,
          name: product.productUnit.description,
        });
      }

      if (product.productConversionUnitItems?.length) {
        product.productConversionUnitItems.forEach(item => {
          uoms.push({
            id: item.uom.id,
            name: item.uom.description,
          });
        });
      }

      const unique = uoms.filter(
        (uom, index, self) => index === self.findIndex(t => t.id === uom.id)
      );

      return unique;
    },
    onProductNoAndNameChange(row) {
      row.item.productUnit = null;

      if (row.item.productNoAndName?.id) {
        const product = this.productList.find(
          p => p.productId === row.item.productNoAndName.id
        );

        row.item.productUnit = product.productUnit
          ? {
              id: product.productUnit.id,
              name: product.productUnit.description,
            }
          : null;

        this.conversionUnitRate.push({
          id: product.productUnit.id,
          rate: 1,
        });

        if (product.productConversionUnitItems?.length) {
          product.productConversionUnitItems.forEach(item => {
            // this.conversionUnitRate.push({
            //   id: item.uom.id,
            //   rate: 1 / item.qtyPerUnitOfMeasure,
            // });

            const qty = new BigNumber(item.qtyPerUnitOfMeasure || 0);

            if (!qty.isFinite() || qty.isZero()) {
              return;
            }
            const rate = new BigNumber(1)
              .div(qty)
              .decimalPlaces(2, BigNumber.ROUND_HALF_UP);
            this.conversionUnitRate.push({
              id: item.uom.id,
              rate: rate.toNumber(),
            });
          });
        }

        const available = product?.productAvailableStock ?? 0;
        row.item.productAvailableStock = available;
        this.$set(row.item, "adjustmentAmount", 0);
        this.$set(row.item, "currentStockToAfterAdjustment", available);
      } else {
        row.item.productAvailableStock = undefined;

        this.$set(row.item, "adjustmentAmount", 0);
        row.item.currentStockToAfterAdjustment = undefined;
      }

      const rowValidation = this.$v.productAdjustmentItems?.$each?.[row.index];
      if (rowValidation?.productUnit?.$reset) {
        rowValidation.productUnit.$reset();
      }
      if (rowValidation?.adjustmentAmount?.$reset) {
        rowValidation.adjustmentAmount.$reset();
      }
      if (rowValidation?.currentStockToAfterAdjustment?.$reset) {
        rowValidation.currentStockToAfterAdjustment.$reset();
      }
    },
    onProductUnitChange(row) {
      if (row.item.productUnit?.id) {
        const product = this.productList.find(
          p => p.productId === row.item.productNoAndName.id
        );

        const available = product?.productAvailableStock ?? 0;

        const conversionRate = this.conversionUnitRate.find(
          r => r.id === row.item.productUnit.id
        );

        if (conversionRate) {
          const availableAfterConversion = available * conversionRate.rate;
          row.item.productAvailableStock = availableAfterConversion;
          this.$set(row.item, "adjustmentAmount", 0);
          this.$set(
            row.item,
            "currentStockToAfterAdjustment",
            availableAfterConversion
          );
        }
      } else {
        row.item.productAvailableStock = undefined;
        this.$set(row.item, "adjustmentAmount", 0);
        row.item.currentStockToAfterAdjustment = undefined;
      }
    },
    isCurrentStockToAfterAdjustmentInvalid(row) {
      const rowV = this.$v.productAdjustmentItems?.$each?.[row.index];
      return !!(
        rowV &&
        rowV.currentStockToAfterAdjustment &&
        rowV.currentStockToAfterAdjustment.$error
      );
    },
    createEmptyAdjustmentItem() {
      return {
        productNoAndName: "",
        adjustmentAmount: 0,
        productUnit: null,
        currentStockToAfterAdjustment: null,
      };
    },
    addAdjustmentItem() {
      this.productAdjustmentItems.push(this.createEmptyAdjustmentItem());
    },
    deleteAdjustmentItem(item, index) {
      this.productAdjustmentItems.splice(index, 1);
    },
    convertUnitStock(row) {
      return row.item.productAvailableStock;
    },
    getProductOptions(currentIndex) {
      const selectedInOtherRows = new Set(
        this.productAdjustmentItems
          .map((item, i) =>
            i !== currentIndex && item.productNoAndName?.id
              ? item.productNoAndName.id
              : null
          )
          .filter(Boolean)
      );
      return this.productOptions.filter(
        product => !selectedInOtherRows.has(product.id)
      );
    },
    getProductSelectedItems() {
      this.productAdjustmentItems = this.selectedProducts.map(item => {
        const productUnit = item.productUnit
          ? {
              id: item.productUnit.id,
              name: item.productUnit.description || item.productUnit.name,
            }
          : null;

        const available = item.productAvailableStock ?? item.productStock ?? 0;

        return {
          productNoAndName: {
            id: item.productId,
            name: `${item.productCode || ""} - ${item.productNameMain || ""}`,
          },
          productUnit,
          productAvailableStock: available,
          adjustmentAmount: 0,
          currentStockToAfterAdjustment: available,
        };
      });

      this.conversionUnitRate = [];
      this.selectedProducts.forEach(product => {
        if (product.productUnit) {
          this.conversionUnitRate.push({
            id: product.productUnit.id,
            rate: 1,
          });
        }
        if (product.productConversionUnitItems?.length) {
          product.productConversionUnitItems.forEach(item => {
            const qty = new BigNumber(item.qtyPerUnitOfMeasure || 0);

            if (!qty.isFinite() || qty.isZero()) {
              return;
            }
            const rate = new BigNumber(1)
              .div(qty)
              .decimalPlaces(2, BigNumber.ROUND_HALF_UP);
            this.conversionUnitRate.push({
              id: item.uom.id,
              rate: rate.toNumber(),
            });
          });
        }
      });
    },
  },
};
</script>

<style scoped>
::v-deep .align-label .col-form-label {
  padding-top: 0px !important;
}

::v-deep .multiselect__tags {
  background-color: #ccecff6c;
  box-shadow: none;
  overflow: hidden;
}

::v-deep .multiselect__single,
::v-deep .multiselect__input,
::v-deep .multiselect__placeholder {
  background-color: transparent !important;
  overflow: hidden;
}

::v-deep span.multiselect__placeholder {
  margin-left: 6px !important;
}

::v-deep .multiselect__input,
::v-deep .multiselect__single {
  padding-left: 6px;
  overflow: hidden;
}

::v-deep span.multiselect__single {
  margin-left: 6px !important;
  overflow: hidden;
}

::v-deep .multiselect__single {
  margin-bottom: 0;
  padding-left: 0;
  overflow: hidden;
}

::v-deep .multiselect__tags {
  box-shadow: none;
}

::v-deep .multiselect__element .multiselect__option {
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
}

::v-deep #productSelectedTable tbody tr td {
  border-top: none !important;
  border-bottom: 1px solid #dee2e6 !important;
}

::v-deep #productSelectedItemModal .modal-body {
  padding-bottom: 0 !important;
}

::v-deep #productSelectedTable tbody tr:last-child td {
  border-bottom: none !important;
}

::v-deep .bottom-add-row {
  text-align: left !important;
  margin-top: 15px;
}

::v-deep #productSelectedTable.table-hover tbody tr:last-child:hover {
  background-color: transparent !important;
}

::v-deep #productSelectedTable thead tr th:nth-child(1),
::v-deep #productSelectedTable tbody tr:not(:last-child) td:nth-child(1) {
  text-align: center !important;
  vertical-align: middle !important;
}

::v-deep #productSelectedTable .bottom-row-cell {
  text-align: left !important;
}

::v-deep .multiselect-unit .multiselect__tags {
  overflow: visible;
  min-width: 0;
}

::v-deep .multiselect-unit .multiselect__single,
::v-deep .multiselect-unit span.multiselect__single {
  white-space: normal !important;
  word-break: break-word;
  overflow-wrap: break-word;
  overflow: visible !important;
}

::v-deep #productSelectedTable thead tr th,
::v-deep #productSelectedTable tbody tr td {
  vertical-align: middle !important;
}

::v-deep .delete-btn {
  background-color: transparent;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 4px 8px;
  cursor: pointer;
  color: #a3a4a6;
  font-size: 20px;
  width: 48px;
  height: 38px;
}

.delete-btn:hover:not(:disabled) {
  background-color: #d33;
  color: #fff;
}

.delete-btn:disabled {
  border-color: #ebebeb !important;
  color: #ebebeb !important;
  cursor: not-allowed;
}
</style>
